import RadarZeroField from "components/calibration/RadarZeroField";
import SensorSetupFields, { effectiveSensorType } from "components/calibration/SensorSetupFields";
import {
  Badge,
  Field,
  SettingsSection,
  TextInput,
  Toggle,
  UnitInput,
  buttonPrimary,
  buttonSecondary,
} from "components/forms/FormParts";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Cpu,
  Crosshair,
  Loader2,
  Radio,
  RefreshCw,
  Ruler,
  Save,
  Send,
  Tag,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  getDeviceData,
  getDeviceStatus,
  sendMqttConfig,
  updateCalibration,
  updateDevice,
} from "services/api";
import { classifyDeviceStatus } from "utils/deviceStatus";
import {
  WAKE_PRESETS,
  describeInterval,
  epochToLocalInput,
  localInputToEpoch,
  validateIdentity,
} from "utils/deviceConfigForm";

const DEFAULT_FORM = {
  name: "",
  is_active: true,
  eid: "",
  iccid: "",
  distance_1_calibration: 0,
  distance_2_calibration: 0,
  sensor_to_bottom_cm: 3.75,
  height_bottom_to_ground: 0,
  sensor_type: "auto",
  sleeve_overlap_cm: 3.6,
};

const NUMBER_FIELDS = [
  "distance_1_calibration",
  "distance_2_calibration",
  "sensor_to_bottom_cm",
  "height_bottom_to_ground",
  "sleeve_overlap_cm",
];

const formFromConfig = (config) => ({
  name: config.name || "",
  is_active: config.is_active,
  eid: config.eid || "",
  iccid: config.iccid || "",
  distance_1_calibration: config.distance_1_calibration ?? 0,
  distance_2_calibration: config.distance_2_calibration ?? 0,
  sensor_to_bottom_cm: config.sensor_to_bottom_cm ?? 3.75,
  height_bottom_to_ground: config.height_bottom_to_ground ?? 0,
  sensor_type: config.sensor_type || "auto",
  sleeve_overlap_cm: config.sleeve_overlap_cm ?? 3.6,
});

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

const formatAgo = (value) => {
  if (!value) return "never";
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 1440) return `${Math.round(minutes / 60)} h ago`;
  return formatDateTime(value);
};

function StatusItem({ icon: Icon, label, children }) {
  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {label}
      </dt>
      <dd className="mt-1 truncate text-sm font-semibold text-slate-900">{children}</dd>
    </div>
  );
}

/** What the device last reported, read-only, before anything that can be changed. */
function DeviceSummary({ deviceConfig, deviceStatus, onRefresh }) {
  const reported = deviceStatus?.reported;
  const connection = classifyDeviceStatus(reported?.last_seen);
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6" aria-label="Device status">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className={`h-2.5 w-2.5 rounded-full ${connection.dotClassName}`} aria-hidden="true" />
          <h3 className="text-base font-semibold text-slate-900">
            {reported ? `${connection.label} · last heartbeat ${formatAgo(reported.last_seen)}` : "No heartbeat yet"}
          </h3>
        </div>
        <button type="button" onClick={onRefresh} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Refresh status" aria-label="Refresh status">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
        <StatusItem icon={Cpu} label="Firmware">
          {reported?.firmware || "—"}{" "}
          <Link to="/firmware" className="ml-1 text-xs font-medium text-primary hover:underline">
            Update
          </Link>
        </StatusItem>
        <StatusItem icon={Clock} label="Wakes every">
          {reported?.interval ? describeInterval(reported.interval) : "—"}
        </StatusItem>
        <StatusItem icon={deviceStatus?.in_sync === false ? AlertCircle : CheckCircle2} label="Settings on device">
          {!reported ? (
            "—"
          ) : deviceStatus.in_sync ? (
            <span className="text-emerald-700">Up to date</span>
          ) : (
            <span className="text-amber-700">Waiting for the device</span>
          )}
        </StatusItem>
        <StatusItem icon={Zap} label="Boot count">
          {reported?.boot_count != null ? `#${reported.boot_count}` : "—"}
        </StatusItem>
      </dl>
      {deviceConfig && (
        <p className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-500">
          MAC <span className="font-mono text-slate-700">{deviceConfig.mac_address}</span> · Added{" "}
          {formatDateTime(deviceConfig.created_at)}
        </p>
      )}
    </section>
  );
}

/**
 * The wake schedule is not part of Save: it is sent to the device over MQTT at once, so it has its own card, plain
 * units (minutes/hours and a local date-time, not seconds and a Unix timestamp) and a confirmation before sending.
 */
function WakeSchedule({ deviceMac, deviceName, reportedInterval }) {
  const [interval, setIntervalSeconds] = useState(reportedInterval || 3600);
  const [touched, setTouched] = useState(false);
  const [custom, setCustom] = useState(false);
  const [startAt, setStartAt] = useState(() => epochToLocalInput(Math.floor(Date.now() / 1000)));
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Start from what the device runs now, until the user picks something else.
  useEffect(() => {
    if (!touched && reportedInterval) {
      setIntervalSeconds(reportedInterval);
      setCustom(!WAKE_PRESETS.some((p) => p.seconds === reportedInterval));
    }
  }, [reportedInterval, touched]);

  const minutes = Math.round(interval / 60);
  const intervalError = !Number.isFinite(interval) || interval < 60 ? "At least 1 minute." : null;
  const startEpoch = localInputToEpoch(startAt);
  const startError = startEpoch == null ? "Pick a date and time." : null;

  const pick = (seconds) => {
    setTouched(true);
    setCustom(false);
    setIntervalSeconds(seconds);
    setResult(null);
  };

  const send = async () => {
    setSending(true);
    setResult(null);
    try {
      await sendMqttConfig(deviceMac, { interval_seconds: interval, wake_epoch: startEpoch });
      setResult({ ok: true, text: `Sent. The device picks it up the next time it wakes and checks in.` });
      setConfirming(false);
    } catch (err) {
      setResult({ ok: false, text: err.message || "Could not send the schedule." });
    } finally {
      setSending(false);
    }
  };

  return (
    <SettingsSection
      title="Wake schedule"
      icon={Radio}
      description="How often the device wakes up to measure and send data."
      badge={<Badge icon={Send} tone="amber">Sent to the device, not saved with Save</Badge>}
      footer={
        confirming ? (
          <div className="flex w-full flex-wrap items-center justify-between gap-3" role="alertdialog" aria-label="Confirm sending the wake schedule">
            <p className="text-sm text-slate-700">
              Send to <strong>{deviceName}</strong>: wake every <strong>{describeInterval(interval)}</strong>, from{" "}
              <strong>{formatDateTime(startEpoch * 1000)}</strong>?
            </p>
            <div className="flex gap-2">
              <button type="button" className={buttonSecondary} onClick={() => setConfirming(false)} disabled={sending}>
                Cancel
              </button>
              <button type="button" className={buttonPrimary} onClick={send} disabled={sending}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? "Sending…" : "Send now"}
              </button>
            </div>
          </div>
        ) : (
          <>
            {result && (
              <p role="status" className={`mr-auto text-sm ${result.ok ? "text-emerald-700" : "text-rose-600"}`}>
                {result.text}
              </p>
            )}
            <button
              type="button"
              className={buttonSecondary}
              disabled={Boolean(intervalError || startError)}
              onClick={() => {
                setResult(null);
                setConfirming(true);
              }}
            >
              <Send className="h-4 w-4" />
              Send to device…
            </button>
          </>
        )
      }
    >
      <fieldset>
        <legend className="mb-1.5 text-sm font-medium text-slate-700">Wake every</legend>
        <div className="flex flex-wrap gap-2">
          {WAKE_PRESETS.map((preset) => {
            const active = !custom && interval === preset.seconds;
            return (
              <button
                key={preset.seconds}
                type="button"
                aria-pressed={active}
                onClick={() => pick(preset.seconds)}
                className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                  active ? "border-primary bg-primary text-on-primary" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                {preset.label}
              </button>
            );
          })}
          <button
            type="button"
            aria-pressed={custom}
            onClick={() => {
              setTouched(true);
              setCustom(true);
              setResult(null);
            }}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
              custom ? "border-primary bg-primary text-on-primary" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            Custom
          </button>
        </div>
        {custom && (
          <Field label="Custom interval" error={intervalError} className="mt-3 max-w-xs">
            {(id, describedBy, invalid) => (
              <UnitInput
                id={id}
                describedBy={describedBy}
                invalid={invalid}
                unit="min"
                min="1"
                step="1"
                value={Number.isFinite(minutes) ? minutes : ""}
                onChange={(event) => setIntervalSeconds(Math.round(parseFloat(event.target.value) * 60))}
              />
            )}
          </Field>
        )}
        {reportedInterval ? (
          <p className="mt-2 text-xs text-slate-500">The device runs every {describeInterval(reportedInterval)} now.</p>
        ) : null}
      </fieldset>

      <Field
        label="Starting from"
        hint="Your local time. The device lines its wake-ups up with this moment."
        error={startError}
        className="max-w-xs"
      >
        {(id, describedBy, invalid) => (
          <div className="flex gap-2">
            <TextInput
              id={id}
              describedBy={describedBy}
              invalid={invalid}
              type="datetime-local"
              value={startAt}
              onChange={(event) => {
                setStartAt(event.target.value);
                setResult(null);
              }}
            />
            <button
              type="button"
              className={buttonSecondary}
              onClick={() => setStartAt(epochToLocalInput(Math.floor(Date.now() / 1000)))}
            >
              Now
            </button>
          </div>
        )}
      </Field>
    </SettingsSection>
  );
}

/**
 * One device's settings, on the Detail page's Configuration tab. Three kinds of change, each kept visibly apart:
 * - General + Water level setup: edited freely, saved together by the Save bar (shown only when something changed);
 * - Radar zero: saved on its own right away (a staff calibration with its own history);
 * - Wake schedule: sent to the device over MQTT, with a confirmation.
 * `onSaved` runs after a save so the page can reload whatever depends on the config.
 */
export default function DeviceConfigPanel({ deviceMac, isStaff, onSaved }) {
  const [deviceConfig, setDeviceConfig] = useState(null);
  const [latestReading, setLatestReading] = useState(null);
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [saved, setSaved] = useState(DEFAULT_FORM);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const fetchDeviceConfig = async (mac) => {
    try {
      const data = await getDeviceData(mac);
      if (data.device_config) {
        const next = formFromConfig(data.device_config);
        setDeviceConfig(data.device_config);
        setSaved(next);
        setForm(next);
      }
      setLatestReading(data.readings?.[0] ?? null);
      setLoadError(null);
    } catch (err) {
      setLoadError(err.message || "Could not load the device's settings.");
    }
  };

  const fetchDeviceStatus = async (mac) => {
    try {
      setDeviceStatus(await getDeviceStatus(mac));
    } catch (err) {
      console.error("Error fetching device status:", err);
    }
  };

  useEffect(() => {
    if (!deviceMac) return undefined;
    setSaveResult(null);
    setDeviceConfig(null);
    setDeviceStatus(null);
    fetchDeviceConfig(deviceMac);
    fetchDeviceStatus(deviceMac);
    const interval = setInterval(() => fetchDeviceStatus(deviceMac), 30000);
    return () => clearInterval(interval);
  }, [deviceMac]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(saved), [form, saved]);
  const identityErrors = validateIdentity(form);
  const missingNumber = NUMBER_FIELDS.find((key) => form[key] === "" || !Number.isFinite(Number(form[key])));
  const blocked = Boolean(identityErrors.name || missingNumber);

  // Leaving the page (reload, closing the tab) with unsaved changes asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const update = (patch) => {
    setSaveResult(null);
    setForm((current) => ({ ...current, ...patch }));
  };

  const save = async (event) => {
    event?.preventDefault();
    if (!deviceMac || !dirty || blocked) return;
    setSaving(true);
    setSaveResult(null);
    try {
      await updateDevice(deviceMac, {
        name: form.name.trim(),
        is_active: form.is_active,
        eid: form.eid.trim(),
        iccid: form.iccid.trim(),
      });
      await updateCalibration(deviceMac, {
        distance_1_calibration: Number(form.distance_1_calibration),
        distance_2_calibration: Number(form.distance_2_calibration),
        sensor_to_bottom_cm: Number(form.sensor_to_bottom_cm),
        height_bottom_to_ground: Number(form.height_bottom_to_ground),
        sensor_type: form.sensor_type,
        sleeve_overlap_cm: Number(form.sleeve_overlap_cm),
      });
      await fetchDeviceConfig(deviceMac);
      setSaveResult({ ok: true, text: "Changes saved." });
      if (onSaved) await onSaved();
    } catch (err) {
      setSaveResult({ ok: false, text: err.message || "Could not save the changes." });
    } finally {
      setSaving(false);
    }
  };

  if (loadError && !deviceConfig) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700" role="alert">
        {loadError}{" "}
        <button type="button" onClick={() => fetchDeviceConfig(deviceMac)} className="ml-2 font-semibold underline">
          Try again
        </button>
      </div>
    );
  }

  if (!deviceConfig) {
    return (
      <div className="flex items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white p-10 text-sm text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading settings…
      </div>
    );
  }

  const isRadar = effectiveSensorType(form, deviceConfig) === "radar";

  return (
    <div className="space-y-6 pb-24">
      <DeviceSummary deviceConfig={deviceConfig} deviceStatus={deviceStatus} onRefresh={() => fetchDeviceStatus(deviceMac)} />

      <form onSubmit={save} className="space-y-6" aria-label="Device settings">
        <SettingsSection
          title="General"
          icon={Tag}
          description="How this device is named and listed, and its SIM card."
        >
          <Field label="Name" error={identityErrors.name}>
            {(id, describedBy, invalid) => (
              <TextInput
                id={id}
                describedBy={describedBy}
                invalid={invalid}
                value={form.name}
                onChange={(event) => update({ name: event.target.value })}
                placeholder="e.g. Field 3 north"
                className="max-w-md"
              />
            )}
          </Field>
          <Toggle
            checked={form.is_active}
            onChange={(is_active) => update({ is_active })}
            label="Active"
            hint="Turn off to archive the device: it is hidden from the Devices list and fleet counts."
          />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Field label="SIM EID" hint="32 digits, printed on the eSIM." warning={identityErrors.eid}>
              {(id, describedBy) => (
                <TextInput
                  id={id}
                  describedBy={describedBy}
                  inputMode="numeric"
                  value={form.eid}
                  onChange={(event) => update({ eid: event.target.value })}
                  className="font-mono"
                />
              )}
            </Field>
            <Field label="SIM ICCID" hint="19–20 digits." warning={identityErrors.iccid}>
              {(id, describedBy) => (
                <TextInput
                  id={id}
                  describedBy={describedBy}
                  inputMode="numeric"
                  value={form.iccid}
                  onChange={(event) => update({ iccid: event.target.value })}
                  className="font-mono"
                />
              )}
            </Field>
          </div>
        </SettingsSection>

        <SettingsSection
          title="Water level setup"
          icon={Ruler}
          description="The measurements that turn the sensor's distance into a water level. Check the worked-out result against the field."
        >
          <SensorSetupFields
            form={form}
            onChange={update}
            deviceConfig={deviceConfig}
            latestReading={latestReading}
            deviceMac={deviceMac}
            isStaff={isStaff}
            showRadarZero={false}
          />
        </SettingsSection>

        {/* Keeps Enter in a field from doing anything but saving. */}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>

      {isRadar && (
        <SettingsSection
          title="Radar zero"
          icon={Crosshair}
          description="How far below the pipe opening this radar measures from, in cm. Found by a calibration campaign in the Experiment tab; each radar differs by about 2 cm. Changing it only affects new readings."
          badge={<Badge tone="blue">Saves on its own, right away</Badge>}
        >
          <RadarZeroField
            deviceMac={deviceMac}
            isStaff={isStaff}
            reloadKey={deviceConfig.radar_zero_cm}
            onChanged={() => fetchDeviceConfig(deviceMac)}
            bare
          />
        </SettingsSection>
      )}

      <WakeSchedule
        deviceMac={deviceMac}
        deviceName={saved.name || deviceMac}
        reportedInterval={deviceStatus?.reported?.interval}
      />

      {/* The Save bar: shown while there is something to save, or to report how the last save went.
          bottom-20 on phones clears the fixed bottom navigation (h-16). */}
      {(dirty || saveResult) && (
        <div className="sticky bottom-20 z-20 lg:bottom-4" role="region" aria-label="Save changes">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
            <p
              role="status"
              className={`flex items-center gap-2 text-sm ${
                saveResult && !dirty ? (saveResult.ok ? "text-emerald-700" : "text-rose-600") : "text-slate-700"
              }`}
            >
              {dirty ? (
                <>
                  <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" />
                  {blocked ? "Fix the highlighted fields to save." : saveResult && !saveResult.ok ? saveResult.text : "You have unsaved changes."}
                </>
              ) : saveResult?.ok ? (
                <>
                  <CheckCircle2 className="h-4 w-4" /> {saveResult.text}
                </>
              ) : (
                saveResult?.text
              )}
            </p>
            {dirty ? (
              <div className="flex gap-2">
                <button
                  type="button"
                  className={buttonSecondary}
                  disabled={saving}
                  onClick={() => {
                    setForm(saved);
                    setSaveResult(null);
                  }}
                >
                  Discard
                </button>
                <button type="button" className={buttonPrimary} disabled={saving || blocked} onClick={save}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            ) : (
              <button type="button" className="text-sm font-medium text-slate-500 hover:text-slate-800" onClick={() => setSaveResult(null)}>
                Dismiss
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
